import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminComments.css';

const AdminComments = () => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionStatus, setActionStatus] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved'
  const [lastCommentCount, setLastCommentCount] = useState(0);

  useEffect(() => {
    // Request notification permission when component mounts 
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const showNotification = (message) => {
    if (Notification.permission === 'granted') {
      new Notification('Novi komentar', {
        body: message,
        icon: '/favicon.ico' // Make sure you have a favicon
      });
    }
  };

  const fetchComments = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend';
      const endpoint = filter === 'pending' 
        ? `${apiUrl}/api/comments/pending`
        : `${apiUrl}/api/comments/all`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      let filteredComments = response.data.data;
      if (filter === 'approved') {
        filteredComments = filteredComments.filter(comment => 
          comment.status === 'approved' || comment.isApproved
        );
      } else if (filter === 'disapproved') {
        filteredComments = filteredComments.filter(comment => 
          comment.status === 'disapproved'
        );
      }

      // Check if there are new pending comments
      const pendingComments = filteredComments.filter(comment => 
        comment.status === 'pending' || (!comment.status && !comment.isApproved)
      );
      if (pendingComments.length > lastCommentCount) {
        showNotification(`Imate ${pendingComments.length} ${pendingComments.length === 1 ? 'novi komentar' : 'nova komentara'} koji čekaju odobrenje.`);
      }
      setLastCommentCount(pendingComments.length);
      
      setComments(filteredComments);
      setLoading(false);
    } catch (error) {
      setError('Došlo je do greške prilikom učitavanja komentara.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
    // Poll for new comments every 30 seconds
    const interval = setInterval(fetchComments, 30000);
    return () => clearInterval(interval);
  }, [filter]);

  const handleModeration = async (commentId, isApproved) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend';
      await axios.put(
        `${apiUrl}/api/comments/${commentId}/moderate`,
        { isApproved },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );

      setActionStatus({
        type: 'success',
        message: isApproved ? 'Komentar je odobren.' : 'Komentar je odbačen.'
      });

      // Refresh comments list
      fetchComments();

      // Clear status after 3 seconds
      setTimeout(() => setActionStatus(null), 3000);
    } catch (error) {
      setActionStatus({
        type: 'error',
        message: 'Došlo je do greške prilikom moderacije komentara.'
      });
      setTimeout(() => setActionStatus(null), 3000);
    }
  };

  const handleModerationWithStatus = async (commentId, status) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend';
      await axios.put(
        `${apiUrl}/api/comments/${commentId}/moderate`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('adminToken')}`
          }
        }
      );

      setActionStatus({
        type: 'success',
        message: status === 'approved' ? 'Komentar je odobren.' : 
                status === 'disapproved' ? 'Komentar je odbačen.' : 
                'Status komentara je ažuriran.'
      });

      // Refresh comments list
      fetchComments();

      // Clear status after 3 seconds
      setTimeout(() => setActionStatus(null), 3000);
    } catch (error) {
      setActionStatus({
        type: 'error',
        message: 'Došlo je do greške prilikom moderacije komentara.'
      });
      setTimeout(() => setActionStatus(null), 3000);
    }
  };

  const handleDelete = async (commentId) => {
    if (window.confirm('Da li ste sigurni da želite da obrišete ovaj komentar?')) {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://jakakaomajka.com/backend';
        await axios.delete(
          `${apiUrl}/api/comments/${commentId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('adminToken')}`
            }
          }
        );

        setActionStatus({
          type: 'success',
          message: 'Komentar je uspešno obrisan.'
        });

        // Refresh comments list
        fetchComments();

        // Clear status after 3 seconds
        setTimeout(() => setActionStatus(null), 3000);
      } catch (error) {
        setActionStatus({
          type: 'error',
          message: 'Došlo je do greške prilikom brisanja komentara.'
        });
        setTimeout(() => setActionStatus(null), 3000);
      }
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('sr-RS', options);
  };

  if (loading) {
    return <div className="admin-comments-loading">Učitavanje komentara...</div>;
  }

  if (error) {
    return <div className="admin-comments-error">{error}</div>;
  }

  const pendingCount = comments.filter(c => 
    c.status === 'pending' || (!c.status && !c.isApproved)
  ).length;

  return (
    <div className="admin-comments">
      <h2>Upravljanje komentarima</h2>

      <div className="filter-controls">
        <button 
          className={filter === 'all' ? 'active' : ''} 
          onClick={() => setFilter('all')}
        >
          Svi komentari
        </button>
        <button 
          className={`${filter === 'pending' ? 'active' : ''} ${pendingCount > 0 ? 'has-pending' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Čekaju odobrenje
          {pendingCount > 0 && (
            <span className="notify-badge">
              {pendingCount}
            </span>
          )}
        </button>
        <button 
          className={filter === 'approved' ? 'active' : ''} 
          onClick={() => setFilter('approved')}
        >
          Odobreni
        </button>
        <button 
          className={filter === 'disapproved' ? 'active' : ''} 
          onClick={() => setFilter('disapproved')}
        >
          Odbačeni
        </button>
      </div>

      {actionStatus && (
        <div className={`action-status ${actionStatus.type}`}>
          {actionStatus.message}
        </div>
      )}

      {comments.length === 0 ? (
        <p className="no-comments">
          {filter === 'pending' 
            ? 'Trenutno nema komentara koji čekaju odobrenje.' 
            : filter === 'approved'
            ? 'Trenutno nema odobrenih komentara.'
            : filter === 'disapproved'
            ? 'Trenutno nema odbačenih komentara.'
            : 'Trenutno nema komentara.'}
        </p>
      ) : (
        <div className="comments-grid">
          {comments.map((comment) => (
            <div key={comment._id} className={`comment-card ${!comment.isApproved ? 'pending' : ''}`}>
              <div className="comment-info">
                <span className="comment-author">{comment.author}</span>
                <span className="comment-date">{formatDate(comment.createdAt)}</span>
              </div>
              <div className="comment-email">{comment.email}</div>
              <div className="comment-content">{comment.content}</div>
              {comment.postId && (
                <div className="comment-post">
                  Post: {comment.postId.title || 'Untitled'}
                </div>
              )}
              <div className="comment-status">
                {comment.status === 'approved' || comment.isApproved ? (
                  <span className="status approved">Approved</span>
                ) : comment.status === 'disapproved' ? (
                  <span className="status disapproved">Disapproved</span>
                ) : (
                  <span className="status pending">Pending</span>
                )}
              </div>
              <div className="comment-actions">
                {(comment.status === 'pending' || (!comment.status && !comment.isApproved)) && (
                  <button
                    className="approve-button"
                    onClick={() => handleModeration(comment._id, true)}
                  >
                    Odobri
                  </button>
                )}
                {(comment.status === 'approved' || comment.isApproved) && (
                  <button
                    className="reject-button"
                    onClick={() => handleModeration(comment._id, false)}
                  >
                    Poništi odobrenje
                  </button>
                )}
                {(comment.status === 'pending' || (!comment.status && !comment.isApproved)) && (
                  <button
                    className="disapprove-button"
                    onClick={() => handleModerationWithStatus(comment._id, 'disapproved')}
                  >
                    Odbaci
                  </button>
                )}
                <button
                  className="delete-button"
                  onClick={() => handleDelete(comment._id)}
                >
                  Obriši
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminComments; 